import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import H2 from '@base/H2/H2';
import P1 from '@base/P1/P1';
import H3 from '@base/H3/H3';
import ContentsTable from '@base/ContentsTable/ContentsTable';
import MenusNav from '@base/MenusNav/MenusNav';
import { menus } from '@utils/constants';
import { Spacings } from '@styles/index';
import Layout from '@layout/index';
import LeftContainer from '@cont/CompanyDetails/LeftContainer';
import RightContainer from '@cont/CompanyDetails/RightContainer';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import useWindowSize from '@hooks/useWindowSize';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  grid-column: span 24;
`;

const StyledH3 = styled(H3)`
  margin-bottom: ${Spacings.SPACING_6B};
  padding-top: 100px;
  margin-top: -100px;
`;

const Paragraph = styled(P1)`
  margin-bottom: ${Spacings.SPACING_4B};
  text-align: justify;
`;

const StyledParagraph = styled(Paragraph)`
  margin-bottom: ${Spacings.SPACING_4B};
  ol > li {
    margin-bottom: ${Spacings.SPACING_2B};
    &:first-of-type {
      margin-top: ${Spacings.SPACING_2B};
    }
  }
`;

const ListItem = ({ children }) => (
  <StyledParagraph as="li">{children}</StyledParagraph>
);

const tableData = [
  {
    label: 'A. Eligibility and Participation ',
    id: 'eligibility_and_participation'
  },
  { label: 'B. Personal Information', id: 'personal_information' },
  {
    label: 'C. Consent And Waiver By Participants',
    id: 'consent_and_waiver_by_participants'
  },
  { label: 'D. Ranks', id: 'ranks' },
  { label: 'E. Rewards', id: 'rewards' },
  { label: 'F. Disclaimers', id: 'disclaimers' },
  { label: 'G. Miscellaneous', id: 'miscellaneous' },
  { label: 'H. Contact Us', id: 'contactus' }
];

const RankerRewardTerms = () => {
  const router = useRouter();
  const { pathname } = router;
  const { isMobile } = useWindowSize();
  return (
    <>
      <LeftContainer>
        <MenusNav data={menus} currentHref={pathname} />
      </LeftContainer>
      <RightContainer>
        <Wrapper>
          <div>
            <div>
              <H2 as="h1">
                Unacademy Ranker Reward Program Terms &amp; Conditions
              </H2>
            </div>
            <br />
            <br />
            <ContentsTable data={tableData} />
            <Paragraph>
              SORTING HAT TECHNOLOGIES PRIVATE LIMITED (hereinafter referred to
              as &quot;Unacademy”) has created and formulated a program entitled
              Unacademy Ranker Reward Program (“Program”) to recognize and
              acknowledge the Learners (as defined in Terms and Conditions) of
              Unacademy who have secured top ranks in an exam to which this
              Program is applicable as may be communicated by Unacademy on the
              platform (“Exam(s)”). The Program shall be made available to all
              Learners who will be appearing for the Exam(s), subject to the
              eligibility criteria specified below, these Unacademy Ranker
              Reward Terms and Conditions (“Program Terms”) and any other Exam
              specific terms and conditions that may be communicated to you by
              Unacademy.
            </Paragraph>
            <Paragraph>
              For the purpose of these Program Terms, “Platform Terms” shall
              collectively mean and include Unacademy Terms and Conditions,
              Privacy Policy and any other policies and guidelines made
              available on the Platform, to the extent applicable.
            </Paragraph>
            <Paragraph>
              These Program Terms and Conditions are an electronic record in
              terms of the Information Technology Act, 2000, the Information
              Technology (Amendment) Act, 2008, and other such applicable laws
              and regulations applicable to electronic records, as
              introduced/amended from time to time. These Program Terms along
              with the Platform Terms collectively form a legally binding
              agreement between you, the Learner [i.e., the Participant (as
              defined hereinafter)] and Unacademy in respect of your
              participation in the Program. By clicking on the ‘I agree’ icon or
              by submitting the registration form for participating in the
              Program, you hereby unconditionally consent, agree, and undertake
              to be bound by and adhere to these Program Terms and Platform
              Terms. These Program Terms, subject to modification by Unacademy,
              shall continue to subsist until completion of the Program as
              determined by Unacademy at its sole discretion, and hence, these
              terms and conditions shall continue to apply on the Participants
              for such duration.
            </Paragraph>
            <br />
            <a name={tableData[0].id}>
              <StyledH3>{tableData[0].label}</StyledH3>
            </a>
            <Paragraph>
              All registered Learners of the Unacademy Platform (defined
              hereinafter) shall be eligible to participate in the Program. It
              is hereby clarified that Unacademy is not obligated to make
              available the Program for all the other exam categories and shall
              have the option to make available the Program for one or more exam
              categories in its sole discretion. To participate in the Program,
              Participants will be required to follow the registration process
              that shall be communicated to them either via email sent by
              Unacademy and/or shall be provided on Unacademy’s website
              www.unacademy.com and/or its mobile applications (“Platform”/
              “Unacademy Platform”) and register for the Program in accordance
              thereof, as may be applicable. All sign-up/registration forms must
              be submitted directly by the Participants and where the
              Participants do not fulfil the Age Requirements, then such sign
              ups/registrations should be done with the consent of and under the
              supervision of their parent or legal guardian in accordance with
              the Platform Terms.
            </Paragraph>
            <Paragraph>
              Furthermore, the Learner shall be eligible to participate in the
              Program provided: <br />
              (i) The Learner is eligible to appear for the Exam <br />
              (ii) The Learner has registered for the Program. <br />
              <br />
              Participants understand that to confirm their eligibility they may
              be required to submit scanned copies of their Exam admit card or
              registration card, application number, roll number, registration
              number, date of birth and any other information that may be
              requested by Unacademy at the time of registration and that the
              information they have submitted shall be used by Unacademy to
              check and verify the exam results of the Participants, once it is
              published
            </Paragraph>
            <Paragraph>
              Pursuant to Participants submitting the sign-up/registration
              forms, the information we collected therefrom may be used for
              various purposes of the Program and the Unacademy Platform,
              including to check and verify the exam results of the
              Participants, reach out to them, offer rewards, etc. For more
              details about the information we collect and process, please refer
              the section on ‘Personal Information’ below.
            </Paragraph>
            <Paragraph>
              After the exam results are published, Unacademy, after checking
              and verifying the results of the participants, shall reach out to
              selected rank holders through email and/or phone call or any other
              means. Selected Participants may be entitled to certain rewards
              subject to the selection parameters and criteria as set out in the
              &quot;Rewards” section of the Program Terms, including a chance to
              be featured as a ‘rank-holder’ on the Platform; such offer to
              rewards and recognition shall be communicated by Unacademy, at its
              sole discretion. To know more, please refer to the ‘Rewards’
              section of these Program Terms
            </Paragraph>
            <Paragraph>
              Please note –
              <br />
              (i) By registering for participation in the Program, Participants
              agree to provide their Personal Information as stated hereinabove
              and on the Platform. Any such Personal Information submitted shall
              be collected, stored, processed, and dealt with, in accordance
              with the Platform Terms, including the Privacy Policy of the
              Platform. <br />
              (ii) Unacademy does not charge any fee for registration or
              participation in the Program. Participants must exercise caution
              against any person/ entity that demands money or its equivalent
              for the purpose of participation in the Program. Unacademy shall
              not be liable to Participant(s) for any amount paid in this regard
              and Unacademy disclaims all responsibilities in respect of the
              same
            </Paragraph>
            <br />
            <br />
            <a name={tableData[1].id}>
              <StyledH3>{tableData[1].label}</StyledH3>
            </a>
            <Paragraph>
              A Participant’s Personal Information, including Sensitive Personal
              Information, that may be collected to enable such Participant to
              participate in the Program and enable rendering of Services (as
              defined in the Terms and Conditions) by the Platform, shall be
              collected, processed, and stored in accordance with the
              Unacademy’s Privacy Policy. Participants shall have all the rights
              available to them under the applicable Data Protection Laws (as
              defined in the Privacy Policy) in accordance with the Unacademy
              Privacy Policy. <br />
              Unacademy shall use a Participant’s Personal Information for
              facilitating this Program and rendering the Services with respect
              to the Platform in the most effective manner, to secure the
              Platform and make improvements, to perform market research and
              product analysis, to carry out Unacademy’s obligations towards the
              Participant, and to communicate with the Participant. The said
              communication can either be by calls, text, or emails and for the
              purposes which include transactional, service, advertising or
              promotional related calls or messages about the Program and other
              offers, new products or services made available by Unacademy, and
              the Participant hereby consents to the same.
              <br />
              Any deletion request received from a Participant in respect of
              their Personal Information shall be processed in accordance with
              Unacademy Privacy Policy. <br />
              Notwithstanding the foregoing, in the event a Participant has
              already received a reward prior to a deletion request /
              withdrawing consent for use of its information, Unacademy shall
              comply with such request as per the terms of the Privacy Policy,
              subject to the said Participant refunding/returning the reward
              received by such Participant pursuant to these Program Terms.
            </Paragraph>
            <br />
            <br />
            <a name={tableData[2].id}>
              <StyledH3>{tableData[2].label}</StyledH3>
            </a>
            <Paragraph>
              The Participant hereby consents to the use by Unacademy of his/her
              name, age, photograph, videos, voice recordings, rank, statements
              and/or testimonials (“Personal Attributes”) for the purpose of
              publication, advertising, promotion on Unacademy’s online
              platforms, including but not limited to the Platform, and through
              any mode, medium, platform and/or format, including without
              limitation, in newspapers, magazines, other print media, on
              television, radio, internet and other electronic and/or in
              mailings for educational awareness.
              <br />
              Any statements or Recording (defined later) provided by the
              Participant and any other details pertaining to their use of the
              Platform, content available of the Platform, shall be exclusively
              provided to Unacademy and the Participant warrants to that extent.
              <br />
              Any marketing material created by Unacademy using a Participant’s
              Personal Attributes shall remain the sole property of Unacademy,
              and the Participant shall have no claim of any nature against the
              same.
              <br />
              The Participant understands that the use of his/her Personal
              Attributes by Unacademy shall be made at its sole discretion
              without any further notice to the Participant. Unacademy shall
              have no obligation to consider Participant’s inputs or feedback on
              the use of his/her Personal Attributes.
              <br />
              The Participant shall release, waive, and discharge claims of any
              kind or nature arising out of or relating to the use of his/her
              Personal Attributes against Unacademy or any person or firm
              authorized by Unacademy to publish the same in accordance with the
              terms hereof. Such release, waiver and discharge shall also extend
              to all group companies, affiliated companies, subsidiaries,
              shareholders, directors, officers, employees, agents and assigns
              of Unacademy and any publishers of the Personal Attributes in
              accordance with the terms hereof. Such a release shall be binding
              on the Participant, his/her respective successors, heirs, assigns,
              executors, administrators, spouse and next of kin.
              <br />
              The Participant hereby confirms that the chance provided by
              Unacademy to be recognized as a rank-holder in the Exam in
              accordance with these Program Terms, is sufficient consideration
              for the use of Participant’s Personal Attributes by Unacademy in
              accordance with the terms hereof, and Unacademy shall have no
              liability to pay any consideration for any use of the
              Participant’s Personal Attributes in accordance with these Feature
              Terms
            </Paragraph>
            <br />
            <br />
            <a name={tableData[3].id}>
              <StyledH3>{tableData[3].label}</StyledH3>
            </a>
            <Paragraph>
              The Participant agrees and acknowledges that by registering for
              the Program, the Participant hereby permits Unacademy to claim the
              Participant’s ranks secured in the Exam for the purpose of
              marketing and promotion.
              <br />
              Furthermore, the Participant hereby permits Unacademy to record
              the participation of the Participant , inter alia interactions,
              appearance, felicitation, name, qualification, credentials,
              rankings, testimony, photographs, audio and/or video of your
              participation, as may be applicable (“Recordings”) and use the
              Recordings for the purpose of livestreaming, upload, publications,
              advertising or promotion of Unacademy or its group Companies or
              their online platforms on their online platforms, including but
              not limited to www.unacademy.com, YouTube and other social media
              channels, and other media activities (“Activities”)
            </Paragraph>
            <br />
            <br />
            <a name={tableData[4].id}>
              <StyledH3>{tableData[4].label}</StyledH3>
            </a>
            <Paragraph>
              Upon the results of the Exam being published, Unacademy may check
              the results of the Participants who have registered for the
              Program using the information provided, to check / validate the
              ranks of the Participants in such Exam. <br />
              Subject to the Exam specific selection parameters and criteria as
              may be communicated to the Participant on the Platform, email/SMS
              or via any other mode, Unacademy may, at its sole discretion,
              choose to reward Participants who have secured a rank in the
              Exam(s) including by way of providing a chance to be featured as a
              ‘rank-holder’ on the Platform (“Rewards”). The Participants shall
              be eligible for Rewards under this Program subject to the to the
              following:
            </Paragraph>
            <ol>
              <ListItem>
                The Participants submits, at the time of registration for the
                Program or later as requested by Unacademy, their roll
                number/application number/hall ticket number/registration number
                or/and other relevant details here to retrieve the result of the
                Exams.
              </ListItem>
              <ListItem>
                In order to be eligible for Rewards, Participants will be
                required to share a good quality photograph of themselves, with
                Unacademy, to be used for the purpose of promotional Activities.
              </ListItem>
              <ListItem>
                In the event Unacademy requests the Participant, the Participant
                shall partake in activities such as <br />
                (i) making promotional appearances on Unacademy owned or partner
                channels of Unacademy; <br />
                (ii) promotions on electronic/print media and talk shows as
                requested by Unacademy
              </ListItem>
              <ListItem>
                Participants shall be required to dedicate 3 (three) full days
                to attend and address seminars/webinars/ workshops with current
                and prospective Learners of Unacademy or any other event
                conducted by Unacademy
              </ListItem>
            </ol>
            <Paragraph>
              The Participant understands that obtaining a high rank in the Exam
              does not automatically qualify them for Rewards under the Program
              and shortlisting of Participants is based on selection parameters
              given above and as may be determined by Unacademy in its sole
              discretion.
              <br />
              The shortlisted Participants may be contacted and be required to
              submit additional information / documentation as may be essential
              for Unacademy to provision the Rewards. Unacademy does not accept
              any responsibility for any incorrect and/or incomplete
              communication details provided and/or delay in receipt of
              communication and/or non-receipt of communication. Finally, upon
              collection and verification of the required documentation (as
              required), Rewards shall be processed to the shortlisted
              Participants within timeline as maybe determined by Unacademy at
              its sole discretion.
              <br />
              Unacademy shall be well within its rights to reject/ the
              Participant or choose another Participant in place of the
              originally shortlisted Participant if: (a) the documents or
              details provided by the Participant are
              invalid/incorrect/incomplete/ misleading /untrue or not acceptable
              in the opinion of Unacademy; (b) the Participant has not conformed
              to any of the terms and conditions contained herein; (c) the
              Participant does not sign the consent form or any other documents
              required, if any, by Unacademy concerning the Program; (d) any
              other reason as may be deemed appropriate by Unacademy.
              <br />
              The process of selection, number of Participants shortlisted for
              the Rewards and the type or form of Reward are to be determined
              and announced , and shall be subject to change, at the sole
              discretion of Unacademy and the decision of Unacademy shall be
              final and binding. The Participants are encouraged to check the
              Program Terms and the Platform Terms on a periodic basis to ensure
              that they do not miss out on any updates and modifications
            </Paragraph>
            <br />
            <br />
            <a name={tableData[5].id}>
              <StyledH3>{tableData[5].label}</StyledH3>
            </a>
            <ol>
              <ListItem>
                Unacademy and/or its subsidiaries/affiliates shall not be under
                any obligation to the Participant in relation to the Program and
                the Participant shall have no claims whatsoever against
                Unacademy and/or its subsidiaries/affiliates relating to the
                selection process, gratification, or the running of the Program
              </ListItem>
              <ListItem>
                Unacademy shall not be responsible for any errors or omissions
                in the terms and conditions contained herein. All information
                provided is provided “as is” without warranty of any kind. To
                the fullest extent permitted by applicable law, Unacademy
                disclaims all warranties, express or implied, including without
                limitation, implied warranties of merchantability, fitness for a
                particular purpose, and non-infringement in respect of the
                Program.
              </ListItem>
              <ListItem>
                Unacademy shall have the sole discretion to discontinue/cancel
                the Program or any part thereof at any point in time and the
                Participant shall not raise any claim due to such
                discontinuation or cancellation at any time whatsoever
              </ListItem>
              <ListItem>
                Unacademy shall not be held responsible in case of a server
                shutdown or loss of Participant’s registration and/or other
                data. Unacademy shall not be responsible for any delay in
                receipt or non-receipt or incomplete or corrupt receipt of the
                registration or, other details of the Participant. Unacademy
                shall not be responsible for any technical difficulties or data
                corruption that may arise during the registration process.
                Further, Unacademy shall not be responsible and/or liable in any
                manner whatsoever in case the Participant is unable to register
                for participation in the Program due to failure on part of the
                internet service provider, facility provider, etc
              </ListItem>
            </ol>
            <br />
            <br />
            <a name={tableData[6].id}>
              <StyledH3>{tableData[6].label}</StyledH3>
            </a>
            <Paragraph>
              All other provisions not specifically stated herein, including
              without limitation, provisions pertaining to governing law and
              jurisdiction, indemnity and limitation of liability, shall apply
              to the Participants as is stated under the Terms and Conditions,
              to the extent applicable.
            </Paragraph>
            <a name={tableData[7].id}>
              <StyledH3>{tableData[7].label}</StyledH3>
            </a>
            <Paragraph>
              For any queries/concerns regarding these Program Terms and
              conditions, Participants may write to us by email at
              legal@unacademy.com or by post to:
            </Paragraph>
            <Paragraph>
              <b>Sorting Hat Technologies Pvt Ltd </b>
              <br />
              Maruti Infotech Centre, 3rd Floor, <br />
              A-Block, Domlur, Koramangala Inner Ring Road, <br />
              Bangalore- 560 071, Karnataka, India.
            </Paragraph>
            {isMobile && (
              <>
                <br />
                <br />
                <br />
              </>
            )}
          </div>
        </Wrapper>
      </RightContainer>
    </>
  );
};

RankerRewardTerms.pageInfo = {
  name: 'Unacademy Rankers Reward Terms',
  isMWebSupported: true,
  layoutOptions: {
    hideGoalSelector: true,
    disableDarkMode: true
  }
};

export default RankerRewardTerms;
RankerRewardTerms.Layout = Layout;
RankerRewardTerms.Wrapper = PageReduxProvider;
